import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Empty, PageHeader } from '../../components';
import { apiRequest } from '../../lib/api';
import type {
  BirthdayEntry,
  DashboardSummary,
  RecentLead,
  UpcomingEvent,
} from './types';
import './dashboard.css';

type Resp<T> = { data: T };

const SUMMARY_KEY = ['dashboard', 'summary'] as const;
const RECENT_LEADS_KEY = ['dashboard', 'recent-leads'] as const;
const UPCOMING_EVENTS_KEY = ['dashboard', 'upcoming-events'] as const;
const BIRTHDAYS_KEY = ['dashboard', 'birthdays'] as const;

const RECENT_LEADS_LIMIT = 6;
const UPCOMING_EVENTS_DAYS = 14;
const UPCOMING_EVENTS_LIMIT = 6;
const BIRTHDAYS_DAYS = 30;
const BIRTHDAYS_LIMIT = 8;

export function DashboardPage() {
  const qc = useQueryClient();

  const summaryQ = useQuery({
    queryKey: SUMMARY_KEY,
    queryFn: () => apiRequest<Resp<DashboardSummary>>('/dashboard/summary'),
  });
  const recentLeadsQ = useQuery({
    queryKey: [...RECENT_LEADS_KEY, RECENT_LEADS_LIMIT],
    queryFn: () =>
      apiRequest<Resp<RecentLead[]>>(`/dashboard/recent-leads?limit=${RECENT_LEADS_LIMIT}`),
  });
  const upcomingEventsQ = useQuery({
    queryKey: [...UPCOMING_EVENTS_KEY, UPCOMING_EVENTS_DAYS, UPCOMING_EVENTS_LIMIT],
    queryFn: () =>
      apiRequest<Resp<UpcomingEvent[]>>(
        `/dashboard/upcoming-events?days=${UPCOMING_EVENTS_DAYS}&limit=${UPCOMING_EVENTS_LIMIT}`,
      ),
  });
  const birthdaysQ = useQuery({
    queryKey: [...BIRTHDAYS_KEY, BIRTHDAYS_DAYS, BIRTHDAYS_LIMIT],
    queryFn: () =>
      apiRequest<Resp<BirthdayEntry[]>>(
        `/dashboard/birthdays?days=${BIRTHDAYS_DAYS}&limit=${BIRTHDAYS_LIMIT}`,
      ),
  });

  const summary = summaryQ.data?.data;
  const recentLeads = recentLeadsQ.data?.data ?? [];
  const upcomingEvents = upcomingEventsQ.data?.data ?? [];
  const birthdays = birthdaysQ.data?.data ?? [];

  const isFetching =
    summaryQ.isFetching ||
    recentLeadsQ.isFetching ||
    upcomingEventsQ.isFetching ||
    birthdaysQ.isFetching;

  const generatedLabel = useMemo(() => {
    if (!summary?.generated_at) return null;
    const d = new Date(summary.generated_at);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, [summary?.generated_at]);

  function refreshAll() {
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  }

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={
          generatedLabel
            ? `At-a-glance view of pipeline, follow-ups and upcoming activity. Updated ${generatedLabel}.`
            : 'At-a-glance view of pipeline, follow-ups and upcoming activity.'
        }
        actions={
          <Button onClick={refreshAll} disabled={isFetching}>
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      />

      {summaryQ.error && (
        <div className="alert" style={{ marginBottom: 12 }}>
          {summaryQ.error instanceof Error
            ? summaryQ.error.message
            : 'Failed to load dashboard summary'}
        </div>
      )}

      <KpiGrid summary={summary} loading={summaryQ.isLoading} />

      <div className="dash-row-2">
        <PipelineCard summary={summary} loading={summaryQ.isLoading} />
        <WorkloadCard summary={summary} loading={summaryQ.isLoading} />
      </div>

      <div className="dash-row-2 equal">
        <RecentLeadsCard
          rows={recentLeads}
          loading={recentLeadsQ.isLoading}
          error={recentLeadsQ.error}
        />
        <UpcomingEventsCard
          rows={upcomingEvents}
          loading={upcomingEventsQ.isLoading}
          error={upcomingEventsQ.error}
        />
      </div>

      <BirthdaysCard
        rows={birthdays}
        loading={birthdaysQ.isLoading}
        error={birthdaysQ.error}
      />
    </>
  );
}

/* ─── KPIs ────────────────────────────────────────────────── */

type KpiTone = 'default' | 'warn' | 'danger';

function KpiTile({
  label,
  value,
  hint,
  tone = 'default',
  to,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: KpiTone;
  to?: string;
}) {
  const valueColor =
    tone === 'danger'
      ? 'var(--danger)'
      : tone === 'warn'
      ? 'var(--warn)'
      : 'var(--ink)';
  const inner = (
    <>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={{ color: valueColor }}>
        {value}
      </div>
      {hint && (
        <div className="muted" style={{ fontSize: 12 }}>
          {hint}
        </div>
      )}
    </>
  );
  if (to) {
    return (
      <Link to={to} className="kpi" style={{ textDecoration: 'none', color: 'inherit' }}>
        {inner}
      </Link>
    );
  }
  return <div className="kpi">{inner}</div>;
}

function KpiGrid({
  summary,
  loading,
}: {
  summary: DashboardSummary | undefined;
  loading: boolean;
}) {
  const placeholder = loading || !summary;

  const totals = summary?.totals;
  const followups = summary?.followups;
  const events = summary?.events;
  const overdue = followups?.overdue ?? 0;
  const dueToday = followups?.due_today ?? 0;

  return (
    <div className="dash-kpi-grid">
      <KpiTile label="Leads" value={placeholder ? '—' : totals!.leads} />
      <KpiTile label="Clients" value={placeholder ? '—' : totals!.clients} />
      <KpiTile
        label="Active clients"
        value={placeholder ? '—' : totals!.active_clients}
      />
      <KpiTile label="Contacts" value={placeholder ? '—' : totals!.contacts} />
      <KpiTile
        label="Follow-ups due today"
        value={placeholder ? '—' : dueToday}
        tone={!placeholder && dueToday > 0 ? 'warn' : 'default'}
        hint="View queue →"
        to="/followups?view=today"
      />
      <KpiTile
        label="Follow-ups overdue"
        value={placeholder ? '—' : overdue}
        tone={!placeholder && overdue > 0 ? 'danger' : 'default'}
        hint="View queue →"
        to="/followups?view=overdue"
      />
      <KpiTile label="Events today" value={placeholder ? '—' : events!.today} />
      <KpiTile
        label="Events next 7d"
        value={placeholder ? '—' : events!.upcoming_7d}
      />
    </div>
  );
}

/* ─── Pipeline ────────────────────────────────────────────── */

function PipelineCard({
  summary,
  loading,
}: {
  summary: DashboardSummary | undefined;
  loading: boolean;
}) {
  const buckets = summary?.leads_by_status ?? [];
  const noStatus = summary?.leads_without_status ?? 0;
  const totalKnown = buckets.reduce((acc, b) => acc + b.count, 0) + noStatus;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Lead pipeline</h2>
        <Link to="/leads" className="link" style={{ fontSize: 12 }}>
          View leads →
        </Link>
      </div>
      {loading ? (
        <div className="card-body muted">Loading…</div>
      ) : buckets.length === 0 && noStatus === 0 ? (
        <div className="card-body">
          <Empty
            icon="leads"
            title="No leads yet"
            subtitle="Pipeline counts will appear here as leads are added."
          />
        </div>
      ) : (
        <div className="dash-pipeline-list">
          {buckets.map((b) => (
            <div key={b.id} className="dash-pipeline-row">
              <span className="pipeline-name">
                <span
                  className="pipeline-dot"
                  style={{ background: b.color ?? 'var(--ink-4)' }}
                />
                {b.name}
              </span>
              <span className="pipeline-count">{b.count}</span>
            </div>
          ))}
          {noStatus > 0 && (
            <div className="dash-pipeline-row">
              <span className="pipeline-name">
                <span className="pipeline-dot" />
                <span className="muted">No status</span>
              </span>
              <span className="pipeline-count">{noStatus}</span>
            </div>
          )}
          <div
            className="dash-pipeline-row"
            style={{ background: 'var(--surface-2)' }}
          >
            <span className="pipeline-name muted" style={{ fontSize: 12 }}>
              Total
            </span>
            <span className="pipeline-count">{totalKnown}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Follow-up workload ──────────────────────────────────── */

function WorkloadCard({
  summary,
  loading,
}: {
  summary: DashboardSummary | undefined;
  loading: boolean;
}) {
  const dueToday = summary?.followups.due_today ?? 0;
  const overdue = summary?.followups.overdue ?? 0;
  const eventsToday = summary?.events.today ?? 0;
  const eventsWeek = summary?.events.upcoming_7d ?? 0;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Follow-up workload</h2>
        <Link to="/followups" className="link" style={{ fontSize: 12 }}>
          View queue →
        </Link>
      </div>
      {loading ? (
        <div className="card-body muted">Loading…</div>
      ) : (
        <div className="dash-workload">
          <Link
            to="/followups?view=overdue"
            className={`workload-tile${overdue > 0 ? ' danger' : ''}`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div className="tile-label">Overdue</div>
            <div className="tile-value">{overdue}</div>
            <div className="tile-hint">
              {overdue === 0 ? 'Nothing past due' : 'Pending or in-progress'}
            </div>
          </Link>
          <Link
            to="/followups?view=today"
            className={`workload-tile${dueToday > 0 ? ' warn' : ''}`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div className="tile-label">Due today</div>
            <div className="tile-value">{dueToday}</div>
            <div className="tile-hint">
              {dueToday === 0 ? 'Nothing scheduled' : 'Pending or in-progress'}
            </div>
          </Link>
          <div className="workload-tile">
            <div className="tile-label">Events today</div>
            <div className="tile-value">{eventsToday}</div>
            <div className="tile-hint">Calls, demos, meetings</div>
          </div>
          <div className="workload-tile">
            <div className="tile-label">Events next 7d</div>
            <div className="tile-value">{eventsWeek}</div>
            <div className="tile-hint">Across all owners</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Recent leads ────────────────────────────────────────── */

function RecentLeadsCard({
  rows,
  loading,
  error,
}: {
  rows: RecentLead[];
  loading: boolean;
  error: unknown;
}) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Recent leads</h2>
        <Link to="/leads" className="link" style={{ fontSize: 12 }}>
          View all →
        </Link>
      </div>
      {error ? (
        <div className="card-body">
          <div className="alert">
            {error instanceof Error ? error.message : 'Failed to load recent leads'}
          </div>
        </div>
      ) : loading ? (
        <div className="card-body muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-body">
          <Empty
            icon="leads"
            title="No leads yet"
            subtitle="New leads will show up here automatically."
          />
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Sales</th>
              <th style={{ textAlign: 'right' }}>Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <Link to={`/leads/${row.id}`}>{row.full_name ?? 'Unnamed'}</Link>
                  {row.company && (
                    <div className="muted" style={{ fontSize: 11 }}>
                      {row.company}
                    </div>
                  )}
                </td>
                <td>
                  {row.status ? (
                    <span
                      className="badge"
                      style={{
                        background: (row.status.color ?? '#888') + '22',
                        color: row.status.color ?? 'var(--ink-2)',
                      }}
                    >
                      {row.status.name}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {row.assigned_sales?.full_name ?? <span className="muted">—</span>}
                </td>
                <td style={{ textAlign: 'right' }} className="muted">
                  {formatShortDate(row.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─── Upcoming events ─────────────────────────────────────── */

function UpcomingEventsCard({
  rows,
  loading,
  error,
}: {
  rows: UpcomingEvent[];
  loading: boolean;
  error: unknown;
}) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Upcoming events</h2>
        <Link to="/calendar" className="link" style={{ fontSize: 12 }}>
          View calendar →
        </Link>
      </div>
      {error ? (
        <div className="card-body">
          <div className="alert">
            {error instanceof Error ? error.message : 'Failed to load events'}
          </div>
        </div>
      ) : loading ? (
        <div className="card-body muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-body">
          <Empty
            icon="calendar"
            title="No upcoming events"
            subtitle="Schedule something from the calendar."
          />
        </div>
      ) : (
        <div className="dash-list">
          {rows.map((ev) => (
            <div key={ev.id} className="dash-list-row">
              <div className="row-main">
                <span className="row-title">{ev.title}</span>
                <span className="row-sub">
                  {formatEventWhen(ev)}
                  {ev.owner && <> · {ev.owner.full_name}</>}
                  {ev.event_type && <> · {ev.event_type}</>}
                </span>
              </div>
              <div className="row-meta">{relativeDayLabel(ev.start_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Birthdays ───────────────────────────────────────────── */

const BIRTHDAY_TARGET_PATH: Record<BirthdayEntry['entity_type'], string> = {
  lead: '/leads',
  client: '/clients',
  contact: '/contacts',
};

function BirthdaysCard({
  rows,
  loading,
  error,
}: {
  rows: BirthdayEntry[];
  loading: boolean;
  error: unknown;
}) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Upcoming birthdays</h2>
        <span className="muted" style={{ fontSize: 12 }}>
          Next {BIRTHDAYS_DAYS} days
        </span>
      </div>
      {error ? (
        <div className="card-body">
          <div className="alert">
            {error instanceof Error ? error.message : 'Failed to load birthdays'}
          </div>
        </div>
      ) : loading ? (
        <div className="card-body muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-body">
          <Empty
            icon="gift"
            title="No birthdays in range"
            subtitle="People with a date of birth on file will appear here."
          />
        </div>
      ) : (
        <div className="dash-list">
          {rows.map((b) => {
            const linkTo = `${BIRTHDAY_TARGET_PATH[b.entity_type]}/${b.id}`;
            return (
              <div key={`${b.entity_type}-${b.id}`} className="dash-list-row">
                <div className="row-main">
                  <span className="row-title">
                    <Link to={linkTo}>{b.full_name ?? 'Unnamed'}</Link>
                  </span>
                  <span className="row-sub">
                    <span
                      className="badge gray"
                      style={{ marginRight: 6, textTransform: 'capitalize' }}
                    >
                      {b.entity_type}
                    </span>
                    {formatLongDate(b.next_birthday)}
                    {b.turning_age != null && <> · turns {b.turning_age}</>}
                  </span>
                </div>
                <div className="row-meta">{daysUntilLabel(b.days_until)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── helpers ─────────────────────────────────────────────── */

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function formatLongDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map((s) => parseInt(s, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return ymd;
  const date = new Date(y, m - 1, d, 0, 0, 0, 0);
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function formatEventWhen(ev: UpcomingEvent): string {
  const start = new Date(ev.start_at);
  if (Number.isNaN(start.getTime())) return ev.start_at;
  if (ev.is_all_day) {
    return start.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }
  return start.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function relativeDayLabel(iso: string): string {
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return '';
  const today = new Date();
  const a = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const b = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const diff = Math.round((b - a) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff < 0) return `${Math.abs(diff)}d ago`;
  return `in ${diff}d`;
}

function daysUntilLabel(days: number): string {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `in ${days}d`;
}
